import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SmartFoodSearch } from '@/components/SmartFoodSearch';
import { toast } from 'sonner';
import { Plus, X, Check, AlertTriangle, Sparkles, PenLine } from 'lucide-react';
import { format } from 'date-fns';

const UNITS = ['g', 'ml', 'serving', 'piece', 'slice', 'scoop', 'glass', 'can', 'bottle'];
const UNIT_GRAMS = { g: 1, ml: 1, scoop: 30, glass: 250, can: 330, bottle: 330 };
const gramsFor = (unit, qty, food) =>
  UNIT_GRAMS[unit] != null ? qty * UNIT_GRAMS[unit] : qty * ((food?.serving_grams || 100));

export function NutritionTargets({ summary, reload }) {
  const [foods, setFoods] = useState([]);
  const [picked, setPicked] = useState(null);
  const [qty, setQty] = useState('');
  const [unit, setUnit] = useState('g');
  const [custom, setCustom] = useState({ calories: '', protein: '', carbs: '', fat: '' });
  const [customMode, setCustomMode] = useState(false);
  const [saveFood, setSaveFood] = useState(false);

  if (!summary) return null;

  const t = summary.target || {}; const a = summary.actual || {}; const ms = summary.macro_status || {};
  const hasTarget = (t.calories || 0) > 0;
  const extras = summary.extras || [];

  const dbMatch = (picked && picked.calories_per_100g != null) ? picked : null;
  const typed = (picked?.name || '').trim();
  const q = parseFloat(qty) || 0;
  const grams = dbMatch ? gramsFor(unit, q, dbMatch) : 0;
  const previewCal = dbMatch ? Math.round((dbMatch.calories_per_100g || 0) * grams / 100) : 0;

  const reset = () => { setPicked(null); setQty(''); setUnit('g'); setCustom({ calories: '', protein: '', carbs: '', fat: '' }); setCustomMode(false); setSaveFood(false); };

  const addDb = async () => {
    if (q <= 0) return toast.error('Enter a quantity');
    if (dbMatch.id && dbMatch.source === 'regen') {
      await api.post('/extra-foods', { food_id: dbMatch.id, name: dbMatch.name, quantity: q, unit });
    } else {
      // FatSecret / AI result — log computed macros as a custom entry
      const f = grams / 100;
      await api.post('/extra-foods', {
        name: dbMatch.is_estimate ? `${dbMatch.name} (est.)` : dbMatch.name, is_custom: true,
        calories: Math.round((dbMatch.calories_per_100g || 0) * f),
        protein: Math.round((dbMatch.protein_per_100g || 0) * f),
        carbs: Math.round((dbMatch.carbs_per_100g || 0) * f),
        fat: Math.round((dbMatch.fat_per_100g || 0) * f),
      });
    }
    toast.success(`Added ${dbMatch.name}`);
    reset(); reload?.();
  };

  const addCustom = async () => {
    if (!typed) return toast.error('Enter a food name');
    if (!(parseFloat(custom.calories) > 0)) return toast.error('Enter calories for the custom food');
    const macros = {
      calories: parseFloat(custom.calories) || 0, protein: parseFloat(custom.protein) || 0,
      carbs: parseFloat(custom.carbs) || 0, fat: parseFloat(custom.fat) || 0,
    };
    if (saveFood) {
      await api.post('/custom-foods', { name: typed, serving_label: '1 serving', ...macros });
    }
    await api.post('/extra-foods', { name: typed, is_custom: true, ...macros });
    toast.success(saveFood ? `Added & saved ${typed} to your foods` : `Added ${typed}`);
    reset(); reload?.();
  };

  const del = async (id) => { await api.delete(`/extra-foods/${id}`); reload?.(); };

  const macros = [
    { key: 'calories', label: 'Calories', unit: ' kcal', kind: 'ceiling' },
    { key: 'protein', label: 'Protein', unit: 'g', kind: 'floor' },
    { key: 'carbs', label: 'Carbs', unit: 'g', kind: 'ceiling' },
    { key: 'fat', label: 'Fat', unit: 'g', kind: 'ceiling' },
  ];
  const flags = summary.flags || {};
  const logged = summary.logged || {};
  const remaining = summary.remaining || {};
  const FLAG = { green: '#65A30D', orange: '#FFB800', red: '#FF5C5C', none: '#5B6B7E' };
  const exceeded = macros.filter(m => flags[m.key] === 'red' && m.kind === 'ceiling').map(m => m.label);
  const proteinShort = flags.protein === 'red' && (summary.has_logged);
  const compliance = summary.compliance;

  return (
    <div className="space-y-3">
      {/* Targets / Current / Remaining */}
      <div className="regen-card-elevated p-4" data-testid="nutrition-targets">
        <div className="flex items-center justify-between mb-3">
          <div className="label-mini">Daily Targets {summary.target_source && <span className="text-muted-foreground">· {summary.target_source === 'manual' ? 'coach' : 'meal plan'}</span>}</div>
          {hasTarget && (summary.has_logged
            ? compliance != null && (
              <span className="pill text-xs font-bold" data-testid="nutrition-compliance-pill" style={{ background: `${compliance >= 85 ? '#65A30D' : compliance >= 60 ? '#FFB800' : '#FF5C5C'}1a`, color: compliance >= 85 ? '#65A30D' : compliance >= 60 ? '#FFB800' : '#FF5C5C' }}>
                {compliance}% Nutrition
              </span>)
            : <span className="pill text-[10px] bg-[var(--c-film)] text-muted-foreground">No food logged yet</span>
          )}
        </div>
        {!hasTarget ? (
          <div className="text-sm text-muted-foreground text-center py-3">No targets set yet. Your coach will set your daily targets.</div>
        ) : (
          <div className="space-y-2.5">
            {macros.map(m => {
              const lv = Math.round(logged[m.key] || 0);
              const tv = Math.round(t[m.key] || 0);
              const rem = Math.round(remaining[m.key] || 0);
              const fl = flags[m.key] || 'none';
              const col = FLAG[fl];
              const pct = tv ? Math.min(100, (lv / tv) * 100) : 0;
              return (
                <div key={m.key} data-testid={`target-${m.key}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{m.label}</span>
                    <span className="font-mono">
                      <span style={{ color: col }}>{lv}</span>
                      <span className="text-muted-foreground"> / {tv}{m.unit}</span>
                      <span className="ml-2 inline-block w-2 h-2 rounded-full align-middle" style={{ background: col }} data-testid={`flag-${m.key}-${fl}`} />
                    </span>
                  </div>
                  <div className="mt-1 h-1.5 bg-[var(--c-film)] rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: col }} />
                  </div>
                  <div className="text-[11px] mt-0.5" style={{ color: rem < 0 ? '#FF5C5C' : '#7C8BA0' }}>
                    {rem >= 0 ? `${rem}${m.unit} remaining` : `${Math.abs(rem)}${m.unit} over`}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {hasTarget && exceeded.length > 0 && (
          <div className="mt-3 flex items-start gap-2 rounded-xl bg-[#FF5C5C]/10 px-3 py-2 text-[11px] text-[#FF9A9A]" data-testid="nutrition-warning">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>Over your {exceeded.join(', ')} target today. That's okay — keep it in mind for your next meals.</span>
          </div>
        )}
        {hasTarget && proteinShort && (
          <div className="mt-2 flex items-start gap-2 rounded-xl bg-[#FFB800]/10 px-3 py-2 text-[11px] text-[#FFD27A]" data-testid="protein-hint">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>You're short on protein — add a protein source (chicken, fish, eggs, Greek yogurt) to hit your target.</span>
          </div>
        )}
      </div>

      {/* Log extra food */}
      <div className="regen-card-elevated p-4" data-testid="extra-food-logger">
        <div className="label-mini mb-1">Log Extra Food / Drink</div>
        <p className="text-[11px] text-muted-foreground mb-2.5">Search a food and enter the quantity. Calories and macros are calculated automatically.</p>

        <SmartFoodSearch value={picked?.name || ''} onPick={(v) => { setPicked(v); setCustomMode(false); }} placeholder="Search food (Chicken, Nasi Lemak, Laksa…)" testid="extra-food-search" />

        {/* DB food → quantity + unit + auto preview */}
        {dbMatch && (
          <div data-testid="extra-food-db">
            <div className="flex gap-2 mt-2.5">
              <Input type="number" value={qty} onChange={e => setQty(e.target.value)} placeholder="Quantity" data-testid="extra-food-qty" className="bg-[var(--c-film)] border-[var(--c-hair)] h-11 rounded-xl font-mono flex-1" />
              <Select value={unit} onValueChange={setUnit}>
                <SelectTrigger className="w-28 bg-[var(--c-film)] border-[var(--c-hair)] h-11 rounded-xl" data-testid="extra-food-unit"><SelectValue /></SelectTrigger>
                <SelectContent>{UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
              <Button onClick={addDb} data-testid="add-extra-food-btn" className="h-11 px-4 bg-[#FFB800] text-[#081726] font-semibold rounded-xl"><Plus className="w-4 h-4" /></Button>
            </div>
            {q > 0 && (
              <div className="mt-2 flex items-center gap-2 text-sm rounded-xl bg-[#FFB800]/10 px-3 py-2 flex-wrap" data-testid="extra-food-preview">
                <Sparkles className="w-3.5 h-3.5 text-[#FFB800]" />
                <span className="font-medium">{dbMatch.name}</span>
                <span className="text-muted-foreground">— {q}{['g', 'ml'].includes(unit) ? unit : ` ${unit}`} —</span>
                <span className="font-mono font-bold text-[#FFB800]">{previewCal} kcal</span>
                {dbMatch.is_estimate && <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-[#FFB800]/20 text-[#FFB800]" data-testid="ai-estimate-badge">ESTIMATE</span>}
                {dbMatch.source === 'fatsecret' && <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-[#00C8FF]/20 text-[#00C8FF]">GLOBAL</span>}
              </div>
            )}
          </div>
        )}

        {/* Unknown food → custom food form */}
        {!dbMatch && typed && (
          <div className="mt-2.5" data-testid="extra-food-custom-zone">
            {!customMode ? (
              <button onClick={() => setCustomMode(true)} data-testid="add-custom-food-toggle"
                className="w-full flex items-center justify-center gap-2 rounded-xl border border-dashed border-[var(--c-hair)] hover:border-[#FFB800]/50 py-3 text-sm text-muted-foreground transition">
                <PenLine className="w-4 h-4" /> "{typed}" not found — Add as custom food
              </button>
            ) : (
              <div className="rounded-xl bg-[var(--c-film)] p-3 space-y-2.5" data-testid="custom-food-form">
                <div className="text-sm font-semibold">Custom Food: {typed}</div>
                <div className="grid grid-cols-2 gap-2">
                  <Input type="number" value={custom.calories} onChange={e => setCustom({ ...custom, calories: e.target.value })} placeholder="Calories *" data-testid="custom-cal" className="bg-regen-card border-[var(--c-hair)] h-10 font-mono" />
                  <Input type="number" value={custom.protein} onChange={e => setCustom({ ...custom, protein: e.target.value })} placeholder="Protein (g)" data-testid="custom-protein" className="bg-regen-card border-[var(--c-hair)] h-10 font-mono" />
                  <Input type="number" value={custom.carbs} onChange={e => setCustom({ ...custom, carbs: e.target.value })} placeholder="Carbs (g)" data-testid="custom-carbs" className="bg-regen-card border-[var(--c-hair)] h-10 font-mono" />
                  <Input type="number" value={custom.fat} onChange={e => setCustom({ ...custom, fat: e.target.value })} placeholder="Fat (g)" data-testid="custom-fat" className="bg-regen-card border-[var(--c-hair)] h-10 font-mono" />
                </div>
                <div className="flex gap-2">
                  <Button onClick={addCustom} data-testid="add-custom-food-btn" className="flex-1 h-10 bg-[#FFB800] text-[#081726] font-semibold"><Plus className="w-4 h-4 mr-1" /> Add Custom Food</Button>
                  <Button onClick={() => setCustomMode(false)} variant="outline" className="h-10 border-[var(--c-hair)]"><X className="w-4 h-4" /></Button>
                </div>
                <button type="button" onClick={() => setSaveFood(s => !s)} data-testid="save-custom-food-toggle"
                  className="flex items-center gap-2 text-[11px] text-muted-foreground hover:text-[#FFB800] transition">
                  <span className={`w-4 h-4 rounded border flex items-center justify-center ${saveFood ? 'bg-[#FFB800] border-[#FFB800]' : 'border-[var(--c-hair)]'}`}>
                    {saveFood && <Check className="w-3 h-3 text-[#081726]" />}
                  </span>
                  Save to my foods (⭐ reuse it next time)
                </button>
              </div>
            )}
          </div>
        )}

        {extras.length > 0 && (
          <div className="mt-3 space-y-2">
            {extras.map(e => (
              <div key={e.id} className="flex items-center gap-3 rounded-xl bg-[var(--c-film)] p-2.5" data-testid={`extra-food-${e.id}`}>
                <div className="flex-1">
                  <div className="text-sm font-medium">{e.name}{e.is_custom && <span className="text-[10px] text-muted-foreground ml-1.5">custom</span>}</div>
                  {e.quantity ? <div className="text-[11px] text-muted-foreground">{e.quantity} {e.unit}</div> : e.grams ? <div className="text-[11px] text-muted-foreground">{e.grams} g</div> : null}
                </div>
                <span className="font-mono text-sm text-[#FFB800]">{Math.round(e.calories)} kcal</span>
                <button onClick={() => del(e.id)} data-testid={`del-extra-food-${e.id}`} className="text-muted-foreground hover:text-red-400"><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
